import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  getCourseCertificateById,
  getCourseCertificateAttachmentBlob,
  getCourseCertificatePdfBlob,
  getCourseCertificates,
  getCourseCertificatesByInjunctionId,
  getCourseCertificatesByImpositionId,
  getCourseCertificatesByPeriodId,
  openCourseCertificateAttachmentDownload,
  openCourseCertificateDownload,
  updateCourseCertificate,
  type CourseCertificateEvidenceModel,
  type DownloadCourseCertificateAttachmentParams,
  type PaginatedResponse,
  type UpdateCourseCertificateParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export interface UseCourseCertificatesParams {
  userId?: string;
  page?: number;
  pageSize?: number;
}

export interface UseCourseCertificateParams {
  userId?: string;
  id?: string;
}

export interface UseCourseCertificatesByPeriodParams {
  userId?: string;
  periodId?: string;
}

export interface UseCourseCertificatesByInjunctionParams {
  userId?: string;
  injunctionId?: string;
}

export interface UseCourseCertificatesByImpositionParams {
  userId?: string;
  impositionId?: string;
}

export type { UpdateCourseCertificateParams } from "@/api/cle-api";

export interface DownloadCourseCertificateParams {
  userId: string;
  id: string;
}

export const courseCertificatesQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
}: UseCourseCertificatesParams) =>
  ["cle-api", "users", userId, "course-certificates", page, pageSize] as const;

export const courseCertificatesByPeriodQueryKey = ({
  userId,
  periodId,
}: UseCourseCertificatesByPeriodParams) =>
  [
    "cle-api",
    "users",
    userId,
    "course-certificates",
    "by-period",
    periodId,
  ] as const;

export const courseCertificatesByInjunctionQueryKey = ({
  userId,
  injunctionId,
}: UseCourseCertificatesByInjunctionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "course-certificates",
    "by-injunction",
    injunctionId,
  ] as const;

export const courseCertificatesByImpositionQueryKey = ({
  userId,
  impositionId,
}: UseCourseCertificatesByImpositionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "course-certificates",
    "by-imposition",
    impositionId,
  ] as const;

export const courseCertificateQueryKey = ({
  userId,
  id,
}: UseCourseCertificateParams) =>
  ["cle-api", "users", userId, "course-certificates", id] as const;

export const courseCertificatePdfQueryKey = ({
  userId,
  id,
}: UseCourseCertificateParams) =>
  ["cle-api", "users", userId, "pdf", "course-certificates", id] as const;

export const courseCertificateAttachmentPdfQueryKey = ({
  userId,
  id,
  attachmentId,
}: DownloadCourseCertificateAttachmentParams) =>
  [
    "cle-api",
    "users",
    userId,
    "pdf",
    "course-certificates",
    id,
    "attachments",
    attachmentId,
  ] as const;

export const useCourseCertificates = ({
  userId,
  page = 1,
  pageSize = 20,
}: UseCourseCertificatesParams): UseQueryResult<
  PaginatedResponse<CourseCertificateEvidenceModel>,
  Error
> =>
  useQuery({
    queryKey: courseCertificatesQueryKey({ userId, page, pageSize }),
    queryFn: () =>
      getCourseCertificates({
        userId: userId ?? "",
        page,
        pageSize,
      }),
    enabled: !!userId,
  });

export const useCourseCertificatesByPeriod = ({
  userId,
  periodId,
}: UseCourseCertificatesByPeriodParams): UseQueryResult<
  CourseCertificateEvidenceModel[],
  Error
> =>
  useQuery({
    queryKey: courseCertificatesByPeriodQueryKey({ userId, periodId }),
    queryFn: () =>
      getCourseCertificatesByPeriodId({
        userId: userId ?? "",
        periodId: periodId ?? "",
      }),
    enabled: !!userId && !!periodId,
  });

export const useCourseCertificatesByInjunction = ({
  userId,
  injunctionId,
}: UseCourseCertificatesByInjunctionParams): UseQueryResult<
  CourseCertificateEvidenceModel[],
  Error
> =>
  useQuery({
    queryKey: courseCertificatesByInjunctionQueryKey({ userId, injunctionId }),
    queryFn: () =>
      getCourseCertificatesByInjunctionId({
        userId: userId ?? "",
        injunctionId: injunctionId ?? "",
      }),
    enabled: !!userId && !!injunctionId,
  });

export const useCourseCertificatesByImposition = ({
  userId,
  impositionId,
}: UseCourseCertificatesByImpositionParams): UseQueryResult<
  CourseCertificateEvidenceModel[],
  Error
> =>
  useQuery({
    queryKey: courseCertificatesByImpositionQueryKey({ userId, impositionId }),
    queryFn: () =>
      getCourseCertificatesByImpositionId({
        userId: userId ?? "",
        impositionId: impositionId ?? "",
      }),
    enabled: !!userId && !!impositionId,
  });

export const useCourseCertificate = ({
  userId,
  id,
}: UseCourseCertificateParams): UseQueryResult<
  CourseCertificateEvidenceModel,
  Error
> =>
  useQuery({
    queryKey: courseCertificateQueryKey({ userId, id }),
    queryFn: () =>
      getCourseCertificateById({
        userId: userId ?? "",
        id: id ?? "",
      }),
    enabled: !!userId && !!id,
  });

export const useDownloadCourseCertificate = (): UseMutationResult<
  void,
  Error,
  DownloadCourseCertificateParams
> =>
  useMutation({
    mutationFn: openCourseCertificateDownload,
  });

export const useDownloadCourseCertificateAttachment = (): UseMutationResult<
  void,
  Error,
  DownloadCourseCertificateAttachmentParams
> =>
  useMutation({
    mutationFn: openCourseCertificateAttachmentDownload,
  });

export const useUpdateCourseCertificate = (): UseMutationResult<
  CourseCertificateEvidenceModel,
  Error,
  UpdateCourseCertificateParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCourseCertificate,
    onSuccess: (data, { id, userId }) => {
      queryClient.setQueryData<CourseCertificateEvidenceModel>(
        courseCertificateQueryKey({ userId, id }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(id),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "course-certificates"],
      });
    },
  });
};

/** Bilaget som blob, til forhaandsvisning i en dialog.
 *
 * Adskilt fra download-mutationen: den gemmer filen, denne viser den. Kun
 * aktiveret naar dialogen er aaben, saa et kursusbevis med fem bilag ikke
 * henter fem PDF'er, ingen har bedt om. */
export const useCourseCertificateAttachmentPdf = (
  { userId, id, attachmentId }: DownloadCourseCertificateAttachmentParams,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: courseCertificateAttachmentPdfQueryKey({
      userId,
      id,
      attachmentId,
    }),
    queryFn: () =>
      getCourseCertificateAttachmentBlob({ userId, id, attachmentId }),
    enabled: enabled && !!userId && !!id && !!attachmentId,
  });

export const useCourseCertificatePdf = ({
  userId,
  id,
}: UseCourseCertificateParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: courseCertificatePdfQueryKey({ userId, id }),
    queryFn: () =>
      getCourseCertificatePdfBlob({
        userId: userId ?? "",
        id: id ?? "",
      }),
    enabled: !!userId && !!id,
  });
